import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { createDepotSchema, CreateDepotSchema } from "@/api.schemas";
import { useCreateDepot } from "@/api/depots";
import { DepotType } from "@/api.types";

export function CreateDepotForm() {
  const form = useForm<CreateDepotSchema>({
    resolver: zodResolver(createDepotSchema),
    defaultValues: {
      name: "",
      type: DepotType.WAREHOUSE,
      capacity: { small: 0, medium: 0, large: 0 },
      currentCapacity: { small: 0, medium: 0, large: 0 },
      serviceRadius: 0,
      operatingHours: [{ day: 1, open: "09:00", close: "17:00" }],
      address: {
        line1: "",
        line2: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        location: { lat: 0, lng: 0 },
        plusCode: "",
      },
    },
  });

  const createDepotMutation = useCreateDepot();

  const onSubmit = form.handleSubmit((values) => {
    createDepotMutation.mutate(values, {
      onSuccess: () => {
        form.reset();
      },
      onError: (error) => {
        console.error("Error creating depot:", error);
      },
    });
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Depot Name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select depot type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="HUB">Hub</SelectItem>
                  <SelectItem value="WAREHOUSE">Warehouse</SelectItem>
                  <SelectItem value="MICRO_HUB">Micro Hub</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Address</FormLabel>
          <AutocompleteAddress
            onAddressChange={(address) => {
              if (address) {
                form.setValue("address", {
                  line1: address.addressComponents.line1,
                  line2: address.addressComponents.line2 || "",
                  city: address.addressComponents.city,
                  province: address.addressComponents.state,
                  country: address.addressComponents.country,
                  postalCode: address.addressComponents.postalCode || "",
                  location: address.location,
                  plusCode: "",
                });
              }
            }}
            onLine2Change={(value) => {
              const currentAddress = form.getValues("address");
              if (currentAddress) {
                form.setValue(
                  "address",
                  {
                    ...currentAddress,
                    line2: value,
                  },
                  {
                    shouldValidate: false,
                  }
                );
              }
            }}
            placeholder="Search address"
          />
        </div>

        <Button type="submit">Create Depot</Button>
      </form>
    </Form>
  );
}
